import { useEffect, useState } from "react";
import { ReactComponent as FilterBy } from "assets/images/svgs/filterBy.svg";
import { FiSearch } from "react-icons/fi";
import FilterDrawer from "components/drawers/host-side/FilterDrawer";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import {
  EVENT_TIMEFRAMES,
} from "utils/helpers/enums/shared.enum";
import { EventsFilterDTO } from "utils/helpers/models/host-dtos/events-filter.dto";
import { useDebounce } from "hooks/useDebounce";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import EventCard from "components/cards/event-card";
import { useSelector } from "react-redux";
import Pagination from "components/pagination";
import useRsvp from "../useHooks";
import RsvpBreadcrumb, { RsvpBreadcrumbLink } from "components/breadcrumbs/RsvpBreadcrumb";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import eventNotFoundGif from "assets/images/gifs/event-not-found.gif";

const RsvpExperiences = () => {
  const breadcrumbLinks: RsvpBreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.rsvpHome },
    {
      title: "Experiences",
      path: siteRoutes.rsvpExperiences,
    },
  ];
  const navigate = useNavigate();
  const [filters, setFilters] = useState<EventsFilterDTO>(() => {
    const defaultFilters = new EventsFilterDTO();
    (defaultFilters as any).timeFrame = EVENT_TIMEFRAMES.UPCOMING;
    return defaultFilters;
  });
  const { getRsvpEvent } = useRsvp();
  const debounceSearch = useDebounce(filters.searchTitle, 1000);
  const [data, setData] = useState<EventDTO[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<string>("upcoming");
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  
  const allFilterTabs = [
    {
      title: "Upcoming",
      value: EVENT_TIMEFRAMES.UPCOMING,
      type: "timeFrame",
      id: "upcoming",
    },
    {
      title: "Past",
      value: EVENT_TIMEFRAMES.PAST,
      type: "timeFrame",
      id: "past",
    },
  ];

  const toggleDrawer = () => {
    setDrawerOpen(prev => !prev);
  };

  const getAllEvents = (filters: EventsFilterDTO) => {
    const queryParams: any = {};
    for (let key in filters) {
      if (
        filters[key]?.toString() &&
        filters[key] !== EVENT_TIMEFRAMES.ALL &&
        filters[key] !== ""
      ) {
        queryParams[key] = filters[key];
      }
    }
    getRsvpEvent(setData, queryParams, [], setFilters);

    // Mark initial load as complete after first API call
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    getAllEvents(filters);
  }, [debounceSearch]);

  const handleFilterChange = (event: any) => {
    const { name, value } = event.target;
    filters[name] = value;
    setFilters({ ...filters });
    if (name !== "searchTitle") {
      getAllEvents(filters);
    }
  };

  const handleTabChange = (
    filterName: string,
    value: string,
    tabId: string
  ) => {
    // Reset data immediately to show loading state
    setData([]);

    const newFilters = { ...filters };

    // Clear all drawer-related filters when tab changes
    newFilters.isSession = "";

    // Set only the selected filter
    newFilters[filterName] = value;

    // Reset pagination when changing tabs
    newFilters.page = 0;
    newFilters.size = 12;

    // Clear all applied filters when tab changes
    setAppliedFilters({});

    // Update selected tab
    setSelectedTab(tabId);

    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const onApplyDrawerFilters = (drawerFilters: any) => {
    const newFilters = { ...filters };
    const newAppliedFilters: any = {};

    for (let key in drawerFilters) {
      if (drawerFilters[key] && drawerFilters[key] !== "") {
        newFilters[key] = drawerFilters[key];
        newAppliedFilters[key] = drawerFilters[key];
      } else {
        delete newFilters[key];
        delete newAppliedFilters[key];
      }
    }

    setAppliedFilters(newAppliedFilters);
    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const removeAppliedFilter = (filterKey: string) => {
    const newFilters = { ...filters };
    const newAppliedFilters = { ...appliedFilters };

    delete newFilters[filterKey];
    delete newAppliedFilters[filterKey];

    setAppliedFilters(newAppliedFilters);
    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  // Check if a tab is currently active
  const isTabActive = (tab: any) => {
    return selectedTab === tab.id;
  };

  const handlePageChange = (pageInfo: { selected: number, limit: number }) => {
    filters.page = pageInfo.selected;
    filters.size = pageInfo.limit;
    getAllEvents(filters);
  };

  return (
    <div className="container min-h-screen mb-10">
      <RsvpBreadcrumb links={breadcrumbLinks} />
      <h2 className="text-2xl font-bold mb-4 mt-6">Experiences</h2>
      <div className="flex justify-between  flex-wrap items-center gap-5">
        <div className="flex flex-wrap gap-5 items-center">
          <div className="flex space-x-4 md:space-x-10 pb-2">
            {allFilterTabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => handleTabChange(tab.type, tab.value, tab.id)}
                className={`text-base font-semibold pb-1 flex items-center ${
                  isTabActive(tab)
                    ? "text-rsvpGreen border-b-2 border-rsvpGreen"
                    : "text-grayText hover:text-rsvpGreen"
                }`}
              >
                {tab.title}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="search"
              name="searchTitle"
              placeholder="Find Experiences"
              value={filters.searchTitle}
              onChange={handleFilterChange}
              className="w-full pl-10 pr-4 py-2 h-[51px] rounded-lg border border-inputsBorderColor outline-none"
            />
          </div>
          <button
            onClick={toggleDrawer}
            className="text-labelTextColor flex gap-1 font-medium"
          >
            Filter by <FilterBy />
          </button>

          {/* Applied Filters Display */}
          {Object.keys(appliedFilters).length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {Object.entries(appliedFilters).map(([key, value]) => {
                const getDisplayLabel = (key: string, value: any) => {
                  if (key === "isSession") {
                    return value === "true" ? "Sessions" : "Workshops";
                  }
                  if (key === "ageGroups") {
                    return `${value}`.replace(/_/g, " ");
                  }
                  return `${key}: ${value}`;
                };

                return (
                  <div
                    key={key}
                    className="flex items-center gap-1 bg-rsvpGreen/10 text-rsvpGreen px-3 py-1 rounded-full text-sm"
                  >
                    <span className="capitalize">
                      {getDisplayLabel(key, value)}
                    </span>
                    <button
                      onClick={() => removeAppliedFilter(key)}
                      className="text-red-500 hover:text-red-700 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 my-6">
        {data.map((event) => (
          <EventCard event={event} key={event.id} showBookmark={false} />
        ))}
      </div>

      <div className="flex items-center justify-center">
        <CustomMessageDisplay
          show={!isLoading && !data.length && !isInitialLoad}
          title={
            selectedTab === "upcoming"
              ? "No Upcoming Experiences"
              : selectedTab === "past"
              ? "No Past Experiences"
              : "No Experiences Found"
          }
          slogan={
            selectedTab === "upcoming"
              ? "You have no upcoming experiences scheduled."
              : selectedTab === "past"
              ? "Your past experiences will appear here once they're completed"
              : "No experiences available at the moment"
          }
          image={eventNotFoundGif}
          imageAlt="No experiences found"
          className="py-12"
          imageClassName="w-[150px] h-[120px]"
          titleClassName="text-2xl font-bold text-blackie"
          sloganClassName="text-gray-500 text-base"
        />
      </div>

      {data?.length > 0 && (
        <Pagination
          count={filters.totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={'#DE9C27'}
          activeTextColor={'#ffffff'}
        />
      )}
      
      {drawerOpen && (
        <FilterDrawer
          isOpen={drawerOpen}
          onClose={toggleDrawer}
          onApply={onApplyDrawerFilters}
          currentFilters={appliedFilters}
        />
      )}


    </div>
  );
};

export default RsvpExperiences;
